import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Rating,
  TextField,
  IconButton,
  CircularProgress,
  Alert,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Close, Star, Home, Wc, Person } from '@mui/icons-material';
import api from '../utils/api';
import useAnalytics from '../analytics/useAnalytics';
import { EVT_RATING_SUBMIT } from '../analytics/events';
import BottomSheet from './shared/BottomSheet';

const RATING_LABELS = {
  1: 'Poor',
  2: 'Fair',
  3: 'Good',
  4: 'Very Good',
  5: 'Excellent',
};

function RatingDialog({ open, onClose, bookingId, libraryName, onSuccess, libraryId }) {
  const { track } = useAnalytics();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [ambienceRating, setAmbienceRating] = useState(0);
  const [washroomRating, setWashroomRating] = useState(0);
  const [librarianRating, setLibrarianRating] = useState(0);
  const [reviewText, setReviewText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (ambienceRating === 0 || washroomRating === 0 || librarianRating === 0) {
      setError('Please rate all categories');
      return;
    }

    try {
      setLoading(true);
      setError('');

      await api.post('/ratings', {
        booking_id: bookingId,
        ambience_rating: ambienceRating,
        washroom_rating: washroomRating,
        librarian_rating: librarianRating,
        review_text: reviewText || null,
      });

      track(EVT_RATING_SUBMIT, { libraryId: libraryId || null });
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      console.error('Error submitting rating:', err);
      setError(err.response?.data?.message || 'Failed to submit rating');
    } finally {
      setLoading(false);
    }
  };

  const overallRating =
    ambienceRating && washroomRating && librarianRating
      ? ((ambienceRating + washroomRating + librarianRating) / 3).toFixed(1)
      : null;

  const content = (
    <>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Ambience */}
      <Box sx={{ mb: 3 }}>
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <Home color="primary" />
          <Typography variant="subtitle1" fontWeight={600}>
            Ambience
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={2}>
          <Rating
            value={ambienceRating}
            onChange={(_, v) => setAmbienceRating(v)}
            size="large"
            icon={<Star fontSize="inherit" />}
            emptyIcon={<Star fontSize="inherit" />}
          />
          {ambienceRating > 0 && (
            <Typography variant="body2" color="text.secondary">
              {RATING_LABELS[ambienceRating]}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Washroom */}
      <Box sx={{ mb: 3 }}>
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <Wc color="primary" />
          <Typography variant="subtitle1" fontWeight={600}>
            Washroom/Facilities
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={2}>
          <Rating
            value={washroomRating}
            onChange={(_, v) => setWashroomRating(v)}
            size="large"
            icon={<Star fontSize="inherit" />}
            emptyIcon={<Star fontSize="inherit" />}
          />
          {washroomRating > 0 && (
            <Typography variant="body2" color="text.secondary">
              {RATING_LABELS[washroomRating]}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Librarian */}
      <Box sx={{ mb: 3 }}>
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <Person color="primary" />
          <Typography variant="subtitle1" fontWeight={600}>
            Staff/Librarian
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={2}>
          <Rating
            value={librarianRating}
            onChange={(_, v) => setLibrarianRating(v)}
            size="large"
            icon={<Star fontSize="inherit" />}
            emptyIcon={<Star fontSize="inherit" />}
          />
          {librarianRating > 0 && (
            <Typography variant="body2" color="text.secondary">
              {RATING_LABELS[librarianRating]}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Overall */}
      {overallRating && (
        <Box
          sx={{
            p: 2,
            mb: 3,
            borderRadius: 2,
            bgcolor: 'primary.soft',
            border: '1px solid',
            borderColor: 'primary.main',
            textAlign: 'center',
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Overall Rating
          </Typography>
          <Typography variant="h4" fontWeight={700} color="primary.main">
            {overallRating}
          </Typography>
        </Box>
      )}

      {/* Review text */}
      <TextField
        fullWidth
        multiline
        rows={3}
        label="Write a review (optional)"
        value={reviewText}
        onChange={(e) => setReviewText(e.target.value)}
        placeholder="Share your experience..."
        inputProps={{ maxLength: 1000 }}
        helperText={`${reviewText.length}/1000 characters`}
      />

      {/* Action buttons */}
      <Box sx={{ display: 'flex', gap: 1, mt: 3 }}>
        <Button onClick={onClose} variant="text" disabled={loading} sx={{ flex: 1 }}>
          Skip
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || ambienceRating === 0 || washroomRating === 0 || librarianRating === 0}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Star />}
          sx={{ flex: 2, borderRadius: 2 }}
        >
          {loading ? 'Submitting...' : 'Submit Rating'}
        </Button>
      </Box>
    </>
  );

  if (isMobile) {
    return (
      <BottomSheet open={open} onClose={onClose} title={`Rate: ${libraryName}`}>
        {content}
      </BottomSheet>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 4 } }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6" fontWeight={700}>
              Rate Your Experience
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {libraryName}
            </Typography>
          </Box>
          <IconButton onClick={onClose} disabled={loading} aria-label="Close">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>{content}</DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }} />
    </Dialog>
  );
}

export default RatingDialog;
